// web/src/routes/KidEdit.tsx
// Route: /kids/:kidId/edit
// M2 (overnight build 2026-05-12). Mirrors KidAdd.tsx, pre-filled from server.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FormField } from '../components/FormField';
import { Button } from '../components/Button';
import { KidPhotoUploader } from '../components/KidPhotoUploader';
import { useKidApi } from '../api/kid';
import { ApiError } from '../api/client';
import { isValidPastDob } from '../lib/age';

export function KidEdit() {
  const { kidId } = useParams<{ kidId: string }>();
  const navigate = useNavigate();
  const api = useKidApi();

  const [loaded, setLoaded] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  // Position + kit colour removed from the kid profile UI 2026-05-19.
  // Position lives per-match (MatchAdd / MatchRecord). Schema fields kept
  // for back-compat with existing data — we just don't surface them here.
  const [defaultTeamName, setDefaultTeamName] = useState('');
  const [hasPhoto, setHasPhoto] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    if (!kidId) return;
    try {
      const kid = await api.get(kidId);
      setHasPhoto(!!kid.photoPath);
    } catch { /* ok */ }
  };

  useEffect(() => {
    if (!kidId) return;
    let cancelled = false;
    (async () => {
      try {
        const kid = await api.get(kidId);
        if (cancelled) return;
        setName(kid.name);
        setDob(kid.dob);
        setDefaultTeamName(kid.defaultTeamName ?? '');
        setHasPhoto(!!kid.photoPath);
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        const e = err as ApiError;
        if (e.status === 404) setNotFound(true);
        else setErrors({ form: e.message || 'Could not load.' });
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [kidId, api]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kidId) return;
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Your child's first name.";
    if (!dob) next.dob = 'Date of birth (YYYY-MM-DD).';
    else if (!isValidPastDob(dob)) next.dob = 'Pick a date in the past, within the last 25 years.';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      await api.update(kidId, {
        name: name.trim(),
        dob,
        defaultTeamName: defaultTeamName.trim() || undefined,
      });
      navigate(`/kids/${encodeURIComponent(kidId)}`);
    } catch (err) {
      const e = err as ApiError;
      setErrors(e.fields ?? { form: e.message || 'Could not save. Try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!loaded) return <p style={{ color: 'var(--muted)' }}>Loading…</p>;

  if (notFound) {
    return (
      <div>
        <h1>Not found</h1>
        <p style={{ color: 'var(--muted)' }}>We couldn't find this kid.</p>
        <Link to="/kids" style={{ color: 'var(--green)' }}>← Back to kids</Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <h1 style={{ marginBottom: 24 }}>Edit {name || 'kid'}</h1>

      {kidId ? (
        <div style={{ marginBottom: 24 }}>
          <KidPhotoUploader kidId={kidId} hasPhoto={hasPhoto} onChange={refresh} />
        </div>
      ) : null}

      <FormField
        label="First name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoComplete="off"
        placeholder="Sam"
        error={errors.name}
        required
      />
      <FormField
        label="Date of birth"
        type="date"
        value={dob}
        onChange={(e) => setDob(e.target.value)}
        max={new Date().toISOString().slice(0, 10)}
        error={errors.dob}
        required
      />

      <FormField
        label="Team (optional)"
        value={defaultTeamName}
        onChange={(e) => setDefaultTeamName(e.target.value)}
        autoComplete="off"
        placeholder="Girton U9s"
        hint="Position is set per match, so you can swap depending on the day."
      />

      {errors.form ? <p style={{ color: 'var(--error)', fontSize: 14 }}>{errors.form}</p> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
        <Button label="Save changes" type="submit" loading={submitting} />
        <button
          type="button"
          onClick={() => navigate(`/kids/${encodeURIComponent(kidId || '')}`)}
          style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 14, cursor: 'pointer', padding: 8 }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
